import { useState } from "react";
import {
  useInfiniteBorrowersQuery,
  type BorrowersQuery,
} from "~/types/graphql";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { Button } from "~/components/ui/button";

const formatUSD = (value: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const formatCollateral = (value: string) => {
  return `${Number(value).toFixed(4)} gOHM`;
};

const formatAddress = (address: string) => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const getEtherscanUrl = (address: string) => {
  return `https://etherscan.io/address/${address}`;
};

const columns: ColumnDef<BorrowersQuery["borrowerStats_collection"][number]>[] =
  [
    {
      accessorKey: "borrower",
      header: "Wallet Address",
      cell: ({ row }) => (
        <a
          href={getEtherscanUrl(row.original.borrower)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono"
        >
          {formatAddress(row.original.borrower)}
        </a>
      ),
    },
    {
      accessorKey: "activeLoans",
      header: "Active Loans",
    },
    {
      accessorKey: "totalDefaultedLoans",
      header: "Total Defaulted Loans",
    },
    {
      accessorKey: "totalLoanExtensions",
      header: "Total Loan Extensions",
    },
    {
      accessorKey: "totalLoans",
      header: "Total Loans",
    },
    {
      accessorKey: "totalRepaidLoans",
      header: "Total Repaid Loans",
    },
    {
      accessorKey: "currentInterestDue",
      header: "Current Interest Due",
      cell: ({ row }) => formatUSD(row.original.currentInterestDue),
    },
    {
      accessorKey: "currentCollateral",
      header: "Current Collateral",
      cell: ({ row }) => formatCollateral(row.original.currentCollateral),
    },
    {
      accessorKey: "currentBorrowed",
      header: "Current Borrowed",
      cell: ({ row }) => formatUSD(row.original.currentBorrowed),
    },
  ];

export default function DefaultedLoans() {
  const { data, isLoading } = useInfiniteBorrowersQuery(
    {
      first: 1000,
      skip: 0,
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.borrowerStats_collection.length < 1000) {
          return undefined;
        }
        return { skip: allPages.length * 1000 };
      },
      initialPageParam: { skip: 0 },
    }
  );

  const defaultedLoans =
    data?.pages.flatMap((page) => page.borrowerStats_collection) ?? [];

  if (isLoading || !defaultedLoans) return <div>Loading...</div>;
  return <DataTable data={defaultedLoans} />;
}

const DataTable = ({
  data,
}: {
  data: BorrowersQuery["borrowerStats_collection"];
}) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "currentBorrowed", desc: true },
  ]);

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    enableSorting: true,
    pageCount: Math.ceil(data.length / 25),
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  return (
    <div className="container mx-auto py-10">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-bold">All-Time Borrowers</h1>
          <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">
            Cooler V1
          </span>
        </div>
        <p className="text-muted-foreground">
          All borrowers who have participated in Cooler V1 
        </p>
      </div>
      <div className="bg-card rounded-md p-8 shadow">
        <div className="border rounded-md border-white/10">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={
                        header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : ""
                      }
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: " 🔼",
                        desc: " 🔽",
                      }[header.column.getIsSorted() as string] ?? null}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="border-b border-white/10">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="p-4">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
            <span className="flex items-center gap-1">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
